/**
 * Buckets Brief — the pure half: colours, scale, and the layout that holds it.
 *
 * The JAS Bollinger distribution (`docs/breadth-design.html` § Buckets Brief):
 * per list, the share of its stocks in each bucket 0–11, today against the
 * previous session. The panel is `components/BucketsBriefView.tsx`; the maths
 * is on the server (`qf/buckets_brief.py`).
 */
import { mintUlid, type BucketsBriefConfig, type Layout, type Section } from "@qufolio/model";

export const BUCKETS = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11] as const;

export const DEFAULT_LISTS = ["NYSE", "NASDAQ", "SP1500", "RU2000"] as const;

/**
 * A bucket's colour — the owner's spec, 2026-09-30, from theme tokens so it
 * follows the theme: 0 the text colour (white on a dark theme), 1–3 negative,
 * 4–5 light negative, 6–7 light positive, 8–11 positive. "Light" is the token
 * mixed toward the surface, so it lightens on a dark theme and a light one alike.
 */
export function bucketColor(bucket: number): string {
  if (bucket === 0) return "var(--qf-text)";
  if (bucket <= 3) return "var(--qf-negative)";
  if (bucket <= 5) return "color-mix(in srgb, var(--qf-negative) 55%, var(--qf-surface))";
  if (bucket <= 7) return "color-mix(in srgb, var(--qf-positive) 55%, var(--qf-surface))";
  return "var(--qf-positive)";
}

/** Yesterday's bars — grey, behind today's. */
export const YESTERDAY_COLOR = "color-mix(in srgb, var(--qf-text-muted) 45%, transparent)";

/** The y-axis top: the tallest bar of either day, rounded up to a step of 5. */
export function axisTop(today: readonly number[], yesterday: readonly number[]): number {
  const max = Math.max(5, ...today, ...yesterday);
  return Math.ceil(max / 5) * 5;
}

/** The section's config, defaults filled. */
export function bucketsBriefConfig(section: Section): BucketsBriefConfig | undefined {
  if (section.kind !== "bucketsBrief") return undefined;
  const config = section.config as BucketsBriefConfig | undefined;
  return { lists: config?.lists?.length ? config.lists : [...DEFAULT_LISTS] };
}

/**
 * A new layout holding one Buckets Brief across the whole grid.
 *
 * The way in for now — the owner's Demo2 review — and the same layout a club
 * will publish later as the JAS special layout.
 */
export function bucketsBriefLayout(name: string): Layout {
  return {
    id: mintUlid(),
    name,
    rows: 1,
    columns: 1,
    rowSizes: ["1fr"],
    columnSizes: ["1fr"],
    sections: [
      {
        id: mintUlid(),
        kind: "bucketsBrief",
        row: 0,
        column: 0,
        title: "Buckets Brief",
        config: { lists: [...DEFAULT_LISTS] },
      },
    ],
  };
}

/** Next / previous list on a phone, wrapping. */
export function stepList(index: number, delta: number, count: number): number {
  return (index + delta + count) % count;
}

/*
 * Session dates for the date box and its ‹ › (owner, 2026-10-01). Plain
 * `YYYY-MM-DD` strings, with the arithmetic in UTC so no local offset moves a
 * date by a day.
 */

const dayOf = (iso: string) => new Date(`${iso}T00:00:00Z`);
const isoOf = (d: Date) => d.toISOString().slice(0, 10);
const addDays = (iso: string, n: number) => {
  const d = dayOf(iso);
  d.setUTCDate(d.getUTCDate() + n);
  return isoOf(d);
};

/**
 * A weekend date moved back to its Friday — the session the server would show
 * for it anyway. The browser's calendar cannot grey out days, so a Saturday or
 * Sunday picked there is corrected rather than refused. Anything else,
 * including "", is returned as it came.
 */
export function toWeekday(iso: string): string {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(iso)) return iso;
  const dow = dayOf(iso).getUTCDay();
  return dow === 6 ? addDays(iso, -1) : dow === 0 ? addDays(iso, -2) : iso;
}

/** The weekday after `iso`, skipping a weekend. Holidays are the server's to know. */
export function nextWeekday(iso: string): string {
  const next = addDays(iso, 1);
  const dow = dayOf(next).getUTCDay();
  return dow === 6 ? addDays(next, 2) : dow === 0 ? addDays(next, 1) : next;
}

/** Today on the exchange's clock, `YYYY-MM-DD`. */
export function marketToday(now: Date = new Date()): string {
  return new Intl.DateTimeFormat("en-CA", { timeZone: "America/New_York" }).format(now);
}

/** Replay bounds — the owner's default, and the server's ceiling. */
export const REPLAY_DEFAULT = 20;
export const REPLAY_MAX = 250;
export const REPLAY_SPEEDS = [1, 2, 4] as const;
/** One frame a second at 1x. */
export const REPLAY_STEP_MS = 1000;

interface FrameLike {
  date: string;
  pct: number[];
  stocks: number;
}

/** Frame `i` of a replay as the panel draws it: that session, the one before in grey. */
export function replayFrame(frames: readonly FrameLike[] | null | undefined, i: number) {
  if (!frames || i < 1 || i >= frames.length) return null;
  const today = frames[i]!;
  const prev = frames[i - 1]!;
  return { date: today.date, prevDate: prev.date, today: today.pct, yesterday: prev.pct, stocks: today.stocks };
}

/** How many frames every list can play — the shortest, so all four move together. */
export function replayLength(lists: Record<string, readonly FrameLike[] | null>): number {
  const lengths = Object.values(lists).map((f) => f?.length ?? 0);
  return lengths.length ? Math.min(...lengths) : 0;
}

// ── the PIP ──────────────────────────────────────────────────────────────────

export type PipSize = "small" | "mid";

/** The PIP's share of the plot, per size (owner, 2026-09-30: small and mid). */
export const PIP_FRACTION: Record<PipSize, { w: number; h: number }> = {
  small: { w: 0.3, h: 0.34 },
  mid: { w: 0.45, h: 0.48 },
};

export interface PipPlacement {
  /** Which top position: over the low buckets, the middle, or the high ones. */
  side: "left" | "center" | "right";
  /** True when the bars reach into the PIP wherever it goes. */
  transparent: boolean;
}

/**
 * Where the PIP goes: the top position with the most room above the bars.
 *
 * The PIP covers the top `h` of the plot over some run of buckets; it fits
 * where every bar of either day under it stays below its bottom edge. Of the
 * three runs, the one whose tallest bar is lowest wins — usually the right,
 * since the top buckets are short. When none fits (every bucket tall), it stays
 * right and goes **transparent** so the bars show through (owner's choice).
 */
export function pipPlacement(
  today: readonly number[],
  yesterday: readonly number[],
  top: number,
  size: PipSize,
): PipPlacement {
  const { w, h } = PIP_FRACTION[size];
  const span = Math.max(1, Math.ceil(12 * w));
  const runs: Array<[PipPlacement["side"], number]> = [
    ["right", 12 - span],
    ["left", 0],
    ["center", Math.floor((12 - span) / 2)],
  ];
  const tallest = (start: number) =>
    Math.max(0, ...[...today, ...yesterday].filter((_, i) => i % 12 >= start && i % 12 < start + span));
  let best = runs[0]!;
  let bestHeight = Infinity;
  for (const run of runs) {
    const height = tallest(run[1]);
    if (height < bestHeight) {
      best = run;
      bestHeight = height;
    }
  }
  const bottom = top * (1 - h) * 0.95;
  return { side: best[0], transparent: bestHeight >= bottom };
}

// ── colours, for the screen and for a GIF ────────────────────────────────────

/**
 * Every colour the panel draws with. On screen these are the theme's CSS
 * variables, so a theme switch repaints for free. A GIF frame is a standalone
 * image, where `var()` and `color-mix()` resolve to nothing — so the GIF gets
 * the same palette resolved to real colours (`resolvePalette`).
 */
export interface Palette {
  text: string;
  muted: string;
  border: string;
  surface: string;
  surfaceAlt: string;
  background: string;
  accent: string;
  positive: string;
  negative: string;
  positiveLight: string;
  negativeLight: string;
  yesterday: string;
}

export const CSS_PALETTE: Palette = {
  text: "var(--qf-text)",
  muted: "var(--qf-text-muted)",
  border: "var(--qf-border)",
  surface: "var(--qf-surface)",
  surfaceAlt: "var(--qf-surface-alt)",
  background: "var(--qf-background)",
  accent: "var(--qf-accent)",
  positive: "var(--qf-positive)",
  negative: "var(--qf-negative)",
  positiveLight: "color-mix(in srgb, var(--qf-positive) 55%, var(--qf-surface))",
  negativeLight: "color-mix(in srgb, var(--qf-negative) 55%, var(--qf-surface))",
  yesterday: YESTERDAY_COLOR,
};

/** A bucket's colour in a palette — `bucketColor`'s rule. */
export function bucketColorIn(palette: Palette, bucket: number): string {
  if (bucket === 0) return palette.text;
  if (bucket <= 3) return palette.negative;
  if (bucket <= 5) return palette.negativeLight;
  if (bucket <= 7) return palette.positiveLight;
  return palette.positive;
}

function parseHex(color: string): [number, number, number] | null {
  const m = /^#?([0-9a-f]{6})$/i.exec(color.trim());
  if (!m) return null;
  const n = parseInt(m[1]!, 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

/** `color-mix(in srgb, a p%, b)` on two hex colours; `a` unchanged if either is not hex. */
export function mixHex(a: string, b: string, p: number): string {
  const x = parseHex(a);
  const y = parseHex(b);
  if (!x || !y) return a;
  const c = x.map((v, i) => Math.round(v * p + y[i]! * (1 - p)));
  return `#${c.map((v) => v.toString(16).padStart(2, "0")).join("")}`;
}

/** The palette with every token read from the theme (`get("--qf-text")` → `#e0e0e0`). */
export function resolvePalette(get: (name: string) => string): Palette {
  const t = (name: string, fallback: string) => get(name).trim() || fallback;
  const text = t("--qf-text", "#e0e0e0");
  const muted = t("--qf-text-muted", "#888888");
  const surface = t("--qf-surface", "#1e1e1e");
  const positive = t("--qf-positive", "#26a69a");
  const negative = t("--qf-negative", "#ef5350");
  return {
    text,
    muted,
    border: t("--qf-border", "#333333"),
    surface,
    surfaceAlt: t("--qf-surface-alt", surface),
    background: t("--qf-background", surface),
    accent: t("--qf-accent", "#4fc3f7"),
    positive,
    negative,
    positiveLight: mixHex(positive, surface, 0.55),
    negativeLight: mixHex(negative, surface, 0.55),
    yesterday: mixHex(muted, surface, 0.45),
  };
}
